import { createFileRoute } from "@tanstack/react-router";
import { PageHeader } from "@/components/page-header";
import { DateRangeFilter } from "@/components/date-range-filter";
import { SetupPanel } from "@/components/setup-panel";
import { DataTable } from "@/components/data-table";
import { ChartCard } from "@/components/charts/chart-card";
import { BarChart } from "@/components/charts/bar-chart";
import { DataNote } from "@/components/analytics/report-shell";
import { StoreSampleBanner } from "@/components/analytics/store-sample-banner";
import { SkStorePage } from "@/components/skeleton";
import { PageQuery } from "@/components/page-query";
import { RepeatTiles } from "@/features/repeat/tiles";
import { GoogleAdCard } from "@/features/repeat/google-ad-card";
import { CohortCard } from "@/features/repeat/cohort-card";
import { CustomersCard } from "@/features/repeat/customers-card";
import type { RepeatBuyersPage } from "@shared/pages/repeat-buyers";
import { pageQuery } from "@/lib/queries";
import { useTitle } from "@/lib/title";

export const Route = createFileRoute("/_app/repeat-buyers")({
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(pageQuery<RepeatBuyersPage>("repeat-buyers", deps));
  },
  component: RepeatBuyersRoute,
});

function RepeatBuyersRoute() {
  useTitle("Repeat buyers");
  const search = Route.useSearch();
  return (
    <PageQuery<RepeatBuyersPage> name="repeat-buyers" search={search} skeleton={<SkStorePage row="pair" />}>
      {(page) => <RepeatBuyers page={page} />}
    </PageQuery>
  );
}

function RepeatBuyers({ page }: { page: RepeatBuyersPage }) {
  const { range: r, status } = page;
  const onFile = page.since ? ` Orders on file start ${page.since}; earlier purchases are not visible here.` : "";
  return (
    <div className="page-shell">
      <PageHeader
        title="Repeat buyers"
        info={`Customers whose first order fell in the range, and whether they came back afterwards.${onFile}`}
        actions={<DateRangeFilter presets={r.presets} defaultKey="all" allowAllTime />}
      />
      <StoreSampleBanner sales={page.sources.sales === "sample"} />
      <DataNote status={status} staleAfterDays={3} />
      {status && !status.onFile.orders && (
        <SetupPanel title="No Shopify orders on file yet" className="mb-4">
          Shopify is connected. Repeat buyers show here after the first sync finishes.
        </SetupPanel>
      )}

      <RepeatTiles tiles={page.tiles} />

      <div className="mt-4 grid gap-4 @5xl:grid-cols-5">
        <GoogleAdCard className="@5xl:col-span-2" ads={page.google.ads} other={page.google.other} since={page.since} />
        <ChartCard className="@5xl:col-span-3" title="Where repeat buyers came from" hint="The channel credited with each customer's first order, from Shopify. Came back: ordered again at any later date.">
          <DataTable columns={page.channels.columns} rows={page.channels.rows} rowKey="key" defaultSort="customers" pageSize={12} emptyText="No first-time customers in this range." />
        </ChartCard>
      </div>

      <div className="mt-4">
        <ChartCard title="Bought again" hint="Products a customer bought in two or more orders, over every order on file whatever the range. Shopify sales, from Shopify.">
          <DataTable columns={page.products.columns} rows={page.products.rows} rowKey="key" defaultSort="repeatCustomers" pageSize={10} emptyText="No product has been bought twice by the same customer yet." />
        </ChartCard>
      </div>

      <div className="mt-4 grid gap-4 @5xl:grid-cols-5">
        <ChartCard className="@5xl:col-span-2" title="Time to the next purchase" hint="Customers in this range by how long their second order took.">
          <BarChart
            labels={page.daysToNext.labels}
            series={[{ name: "Customers", color: "var(--primary)", values: page.daysToNext.values }]}
            format="number"
            showValues
            labelEvery={1}
            legend={false}
            emptyText="No first-time customers in this range."
          />
        </ChartCard>
        <CohortCard className="@5xl:col-span-3" cohorts={page.cohorts} />
      </div>

      <div className="mt-4">
        <CustomersCard rows={page.customers} canLookUp={page.canLookUp} sample={page.sources.sales === "sample"} />
      </div>
    </div>
  );
}
